import { Component, QueryList, ViewChildren } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BankruptciesFilterRequest, ManagementContractNoticeAndAnnouncementFilterRequest, ManagementContractNoticeAndAnnouncementResponse, PublicBankruptcyControllerService, PublicContractNoticeControllerService, PublicSaleControllerService, SalesFilterRequest, SimplifiedContractNoticeAndAnnouncementFilterRequest } from 'api-contract-notices';
import { take } from 'rxjs/internal/operators/take';
import { SharedService } from '../../../../shared/services/shared.service';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ContractNoticesFiltersComponent } from '../../../../admin/components/data-administration/contract-notices/contract-notices-filters/contract-notices-filters.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Constants } from '../../../../shared/interfaces/constants';
import { environment } from '../../../../../environments/environment';

@Component({
	selector: 'app-landing-list',
	standalone: true,
	imports: [TranslateModule, MatCardModule, MatTableModule, MatPaginatorModule, ContractNoticesFiltersComponent, MatTooltipModule, MatIconModule, MatButtonModule],
	templateUrl: './landing-list.component.html',
	styleUrl: './landing-list.component.scss',
})
export class LandingListComponent {
	private resellerSettingsContext: any;
  externalAssets = environment.reseller.externalAssets;
	@ViewChildren(MatPaginator) paginator = new QueryList<MatPaginator>();
	pageOptions = [20, 50, 100];

  dataAdministrationType: 'CLIPPING' | 'CONTRACT_NOTICES' | 'CONTRACT_ANNOUNCEMENTS' | 'SALES' | 'BANKRUPTCIES' = 'CONTRACT_NOTICES';
	displayedColumns: string[] = [];
	contractNoticesColumns: string[] = ['actions', 'publishedDate', 'deliveryDate', 'contractAuthority', 'contractSubject', 'estimatedPurchaseValue', 'type', 'medium', 'country'];
	contractAnnouncementsColumns: string[] = [
		'actions',
		'publishedDate',
		'contractAuthority',
		'contractSubject',
		'contractors',
		'estimatedPurchaseValue',
		'agreementValue',
		'type',
		'medium',
		'country',
	];
  salesColumns: string[] = ['actions', 'publicationDate', 'trustees', 'debtors', 'locations', 'placesOfSale', 'saleDate', 'type'];
  bankruptciesColumns: string[] = ['actions', 'publicationDate', 'company', 'phase'];

	dataSource!: MatTableDataSource<any>;

	listType = '';
	dataGetType: 'ALL' | 'FILTER' = 'ALL';
	filtersStore: ManagementContractNoticeAndAnnouncementFilterRequest | undefined = {};
	showFilters = true;
  private readonly DAYS_RANGE = 3;
	private readonly DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	constructor(
		private sharedService: SharedService,
		private utils: UtilsService,
		private route: ActivatedRoute,
		private apiPublicContractNotice: PublicContractNoticeControllerService,
		private translate: TranslateService,
		private router: Router,
    private apiPublicSales: PublicSaleControllerService,
    private apiPublicBankruptcies: PublicBankruptcyControllerService
	) {
		this.sharedService.resellerSettingsContext.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				this.resellerSettingsContext = response;
			},
		});

		this.route.params.pipe(takeUntilDestroyed()).subscribe(params => {
			this.listType = params['type'].toUpperCase().replaceAll('-', '_');

      switch (this.listType) {
        case 'CONTRACT_NOTICES': {
          this.displayedColumns = this.contractNoticesColumns;
          break;
        }
        case 'CONTRACT_ANNOUNCEMENTS': {
          this.displayedColumns = this.contractAnnouncementsColumns;
          break;
        }
        case 'SALES': {
          this.displayedColumns = this.salesColumns;
          break;
        }
        case 'BANKRUPTCIES': {
          this.displayedColumns = this.bankruptciesColumns;
        }
      }

      this.filtersStore = undefined;
      setTimeout(() => { // reset previous filters
        this.itemAction('LIST_ITEMS');
      }, 10);
		});
	}

	itemAction(action: 'LIST_ITEMS', data?: ManagementContractNoticeAndAnnouncementResponse) {
		switch (this.listType) {
			// case 'clipping':
			// 	break;
			case 'CONTRACT_NOTICES':
			case 'CONTRACT_ANNOUNCEMENTS': {
				if (action === 'LIST_ITEMS') {
					const types =
						this.listType === 'CONTRACT_NOTICES'
							? (Constants.CONTRACT_NOTICE_TYPES as Array<ManagementContractNoticeAndAnnouncementFilterRequest.TypesEnum>)
							: (Constants.CONTRACT_ANNOUNCEMENT_TYPES as Array<ManagementContractNoticeAndAnnouncementFilterRequest.TypesEnum>);
					this.filtersStore = {
						types,
					};
					this.getContractNotices(this.dataGetType);
				}
				break;
			}
      case 'SALES': {
        if (action === 'LIST_ITEMS') {
					this.getSales();
				}
        break;
      }
      case 'BANKRUPTCIES': {
        if (action === 'LIST_ITEMS') {
					this.getBankruptcies();
				}
        break;
      }
		}
	}

	parseItemData(element: any, column: string) {
		switch (column) {
			case 'publishedDate':
      case 'deliveryDate':
      case 'publicationDate':
      case 'saleDate': {
				return element[column] ? this.utils.formatDate(element[column]) : null;
			}
			case 'contractAuthority': {
				if (element.noticeType) {
					return this.utils.getParameterByLanguageScript(element[column], 'name', true);
				} else {
					return this.utils.getParameterByLanguageScript(element.contractNotice[column], 'name', true);
				}
			}
      case 'company': {
        return this.utils.getParameterByLanguageScript(element[column], 'name', true);
        break;
      }
			case 'contractSubject': {
				if (element.noticeType) {
					return this.utils.getParameterByLanguageScript(element, 'contractSubject', true);
				} else {
					return this.utils.getParameterByLanguageScript(element.contractNotice, 'contractSubject', true);
				}
			}
			case 'contractors':
      case 'trustees':
      case 'debtors':{
				if (element[column] && element[column].length) {
					const contractors: string[] = [];
					element[column].forEach((c: any) => {
						contractors.push(this.utils.getParameterByLanguageScript(c, 'name', true));
					});
					return contractors.length > 2 ? contractors.slice(0, 1).join(', ') + '...' : contractors.join(', ');
				}
				return [];
			}
			case 'type': {
				if (element.noticeType) {
					return this.translate.instant('CONTRACT_NOTICES.' + element.noticeType);
				} else if (element.announcementType) {
					return this.translate.instant('CONTRACT_ANNOUNCEMENTS.' + element.announcementType);
				} else if (this.listType === 'SALES') {
          return this.translate.instant('SALES.TYPES.' + element.type);
        }
				break;
			}
      case 'phase': {
        return this.translate.instant('BANKRUPTCIES.PHASES.' + element.phase);
      }
			case 'medium': {
				if (element.noticeType) {
					return element.mediums && element.mediums.length ? this.utils.getParameterByLanguageScript(element.mediums[0], 'name') : '';
				} else {
					return element.contractNotice.mediums && element.contractNotice.mediums.length ? this.utils.getParameterByLanguageScript(element.contractNotice.mediums[0], 'name') : '';
				}
			}
			case 'country': {
				if (element.noticeType) {
					return element[column] ? element[column].toLowerCase() : '';
				} else {
					return element.contractNotice[column] ? element.contractNotice[column].toLowerCase() : '';
				}
			}
			case 'estimatedPurchaseValue': {
				if (element.noticeType) {
					return element[column] ? `${this.utils.formatCurrency(element[column])} ${element.currency}` : '';
				} else if (element.announcementType) {
					return element.contractNotice[column] ? `${this.utils.formatCurrency(element.contractNotice[column])} ${element.contractNotice.currency}` : '';
				}
				break;
			}
			case 'agreementValue': {
				if (element.announcementType) {
					return element[column] ? `${this.utils.formatCurrency(element[column])} ${element.currency}` : '';
				}
				return '';
			}
			default: {
				return element[column];
			}
		}
	}

	private getContractNotices(dataGetType: 'ALL' | 'FILTER', startIndex?: number) {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};
		if (startIndex !== undefined) {
			pagination.index = startIndex;
		}

		if (dataGetType !== this.dataGetType) {
			pagination.index = 0;
		}
		this.dataGetType = dataGetType;

    const filtersDto: SimplifiedContractNoticeAndAnnouncementFilterRequest = {
      types: this.filtersStore?.types,
      publishedDateFrom: this.DATE_RANGE_LIMITS.dateFrom.getTime(),
      publishedDateTo: this.DATE_RANGE_LIMITS.dateTo.getTime(),
    }
		this.apiPublicContractNotice
			.getPublicFilteredContractNoticesAndAnnouncements(filtersDto, pagination.index, pagination.pageSize)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSource = new MatTableDataSource<any>(response.content);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = pagination.index;
						this.paginator.toArray()[0].length = response.totalElements || 0;
					}
				},
				error: () => {
					this.dataSource = new MatTableDataSource<any>([]);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].length = 0;
					}
				},
			});
	}

	// actionByTypeSplitter(event: any) {
	// 	this.actionRequestedContractNotices(event);
	// }

	// private actionRequestedContractNotices(event: any) {
	// 	const types =
	// 		this.listType === 'contract-notices'
	// 			? (Constants.CONTRACT_NOTICE_TYPES as Array<ManagementContractNoticeAndAnnouncementRequest.TypesEnum>)
	// 			: (Constants.CONTRACT_ANNOUNCEMENT_TYPES as Array<ManagementContractNoticeAndAnnouncementRequest.TypesEnum>);

	// 	switch (event.action) {
	// 		case 'LOAD': {
	// 			this.filtersStore = {
	// 				types,
	// 			};
	// 			this.getContractNotices('ALL', event.id);
	// 			break;
	// 		}
	// 		case 'FILTER': {
	// 			this.filtersStore = {
	// 				...event.filters,
	// 				types: event.filters.types && event.filters.types.length ? event.filters.types : types,
	// 			};
	// 			this.getContractNotices('FILTER', event.id);
	// 			break;
	// 		}
	// 	}
	// }

  private getSales() {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};
    const filtersDto: SalesFilterRequest = {
      createdAtFrom: this.DATE_RANGE_LIMITS.dateFrom.getTime(),
      createdAtTo: this.DATE_RANGE_LIMITS.dateTo.getTime(),
    }
		this.apiPublicSales
			.getPublicSalesPageable(filtersDto, pagination.index, pagination.pageSize)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSource = new MatTableDataSource<any>(response.content);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = pagination.index;
						this.paginator.toArray()[0].length = response.totalElements || 0;
					}
				},
				error: () => {
					this.dataSource = new MatTableDataSource<any>([]);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].length = 0;
					}
				},
			});
	}

  private getBankruptcies() {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};

    const filtersDto: BankruptciesFilterRequest = {
      createdAtFrom: this.DATE_RANGE_LIMITS.dateFrom.getTime(),
      createdAtTo: this.DATE_RANGE_LIMITS.dateTo.getTime(),
    }
		this.apiPublicBankruptcies
			.getPublicBankruptciesPageable(filtersDto, pagination.index, pagination.pageSize)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSource = new MatTableDataSource<any>(response.content);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = pagination.index;
						this.paginator.toArray()[0].length = response.totalElements || 0;
					}
				},
				error: () => {
					this.dataSource = new MatTableDataSource<any>([]);
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].length = 0;
					}
				},
			});
	}

  gotoLogin() {
    this.router.navigate(['/account/login']);
  }
}
